// 우편함 클라이언트(docs/PARTY-TRADE-PRD.md 1.1·3.6). 서버 우편 API(src/server/mail.ts)를 부르고, 받은 내용을 게임 쪽 apply로 넘긴다.
// 받기 순서: POST /api/mail/claim {id} → 응답 mail을 apply(mail)에 넘긴다(게임 레인의 src/game/mailApply.ts applyMail) → true면 POST /api/mail/ack {id}.
// - apply가 false(가방 가득 등)거나 예외면 ack하지 않는다. 서버는 ack 전까지 같은 우편을 최대 3번 다시 준다(연산 id가 같아 두 번 들어가지 않는다).
// - 이미 claimed인데 ack가 안 된 우편(앞선 접속에서 꺼진 경우)도 목록에 다시 나오며 같은 흐름으로 받는다.
// - 오프라인·서버 오류는 예외 대신 결과 객체로 돌려준다. 쿠키 세션 + JSON + 같은 출처(src/account/client.ts와 같은 fetch 방식).
import type { GearItem } from '../shared/gear';

/** 서버 MailView와 같은 모양 */
export interface MailEntry {
  id: number;
  kind: 'gold' | 'item';
  gold: number;
  item: GearItem | null;
  ref: string;
  title: string;
  createdAt: number;
  expiresAt: number;
  /** 이미 내용을 받았다(ack 전이면 다시 받을 수 있다) */
  claimed: boolean;
}
/** 게임이 우편 내용을 가방·골드에 적용하고 저장했는지(true면 ack) */
export type MailApply = (mail: MailEntry) => Promise<boolean>;

export type MailError = 'offline' | 'rate' | 'expired' | 'not-found' | 'auth' | 'server' | 'other-character';
export type MailListResult = { ok: true; mail: MailEntry[] } | { ok: false; error: MailError };
export interface MailClaimResult {
  id: number;
  /** claim 호출 자체가 성공했는가 */
  ok: boolean;
  /** apply가 true였다(게임 저장에 반영됨) */
  applied: boolean;
  /** 서버가 ack를 받았다 */
  acked: boolean;
  error?: MailError;
  /** applied=false이면 이유(apply가 false/예외) */
  reason?: 'apply-false' | 'apply-threw';
}
export interface MailClaimAllResult {
  /** 처리를 시도한 통수 */
  tried: number;
  /** 받아서 ack까지 끝난 통수 */
  done: number;
  /** 못 받은 통수(가방 가득·ack 실패·오류) */
  failed: number;
  results: MailClaimResult[];
  /** 중간에 멈춘 이유(오프라인·빈도 제한) */
  stopped?: MailError;
}

export type MailFetch = (input: string, init?: RequestInit) => Promise<Response>;
export interface MailClientOptions {
  /** 지금 캐릭터 id(없으면 'legacy' — 캐릭터 시스템 전 저장) */
  characterId?: () => string;
  fetch?: MailFetch;
  /** API 주소 머리(기본: 같은 출처 /api/mail) */
  base?: string;
  timeoutMs?: number;
}

const TIMEOUT_MS = 15_000;

function errorOf(status: number, body: unknown): MailError {
  const code = body && typeof body === 'object' ? String((body as { error?: unknown }).error ?? '') : '';
  if (status === 401 || status === 403) return 'auth';
  if (status === 429 || code === 'rate-limited') return 'rate';
  if (status === 404 || code === 'not-found') return 'not-found';
  // 다른 캐릭터가 먼저 받기 시작한 우편(그 캐릭터로 받아야 한다 — 기한 지남이 아니다)
  if (body && typeof body === 'object' && (body as { reason?: unknown }).reason === 'other-character') return 'other-character';
  if (code === 'expired' || code === 'not-eligible' || status === 409 || status === 410) return 'expired';
  return 'server';
}

export class MailClient {
  /** 마지막으로 받은 목록 */
  mail: MailEntry[] = [];
  /** 바뀔 때마다 오른다(UI가 다시 그릴지 판단) */
  revision = 0;
  /** 받는 중 */
  busy = false;
  private readonly f: MailFetch;
  private readonly base: string;
  private readonly timeoutMs: number;

  constructor(o: MailClientOptions = {}) {
    this.f = o.fetch ?? ((input, init) => fetch(input, init));
    this.base = o.base ?? '/api/mail';
    this.timeoutMs = o.timeoutMs ?? TIMEOUT_MS;
    this.characterId = o.characterId ?? (() => 'legacy');
  }
  /** 받는 캐릭터(장비 장부가 캐릭터별이라 서버가 처음 받은 캐릭터에 우편을 묶는다 — PR #243 Codex P1) */
  private readonly characterId: () => string;

  private async call(method: 'GET' | 'POST', path: string, body?: unknown): Promise<{ ok: boolean; status: number; body: unknown } | null> {
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), this.timeoutMs) : 0;
    try {
      const res = await this.f(this.base + path, {
        method, credentials: 'same-origin', signal: ctl?.signal ?? undefined,
        headers: body === undefined ? undefined : { 'content-type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      let json: unknown = null;
      try { json = await res.json(); } catch { json = null; }
      return { ok: res.ok, status: res.status, body: json };
    } catch {
      return null; // 오프라인·시간 초과
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /** 우편 목록(받지 않은 것 + 받았지만 ack 안 된 것) */
  async list(): Promise<MailListResult> {
    const r = await this.call('GET', '');
    if (!r) return { ok: false, error: 'offline' };
    if (!r.ok) return { ok: false, error: errorOf(r.status, r.body) };
    const arr = (r.body as { mail?: unknown } | null)?.mail;
    const mail = Array.isArray(arr) ? (arr as MailEntry[]).filter((m) => m && typeof m.id === 'number') : [];
    this.mail = mail;
    this.revision++;
    return { ok: true, mail };
  }

  /** 한 통 받기: claim → apply → (true면) ack */
  async claim(id: number, apply: MailApply): Promise<MailClaimResult> {
    const c = await this.call('POST', '/claim', { id, characterId: this.characterId() });
    if (!c) return { id, ok: false, applied: false, acked: false, error: 'offline' };
    if (!c.ok) return { id, ok: false, applied: false, acked: false, error: errorOf(c.status, c.body) };
    const b = c.body as { mail?: MailEntry; acked?: boolean } | null;
    if (b?.acked) {
      // 이미 다른 창·앞선 접속에서 끝난 우편: 다시 적용하지 않고 목록에서만 뺀다
      this.drop(id);
      return { id, ok: true, applied: true, acked: true };
    }
    const mail = b?.mail;
    if (!mail || typeof mail.id !== 'number') return { id, ok: false, applied: false, acked: false, error: 'server' };
    let applied = false;
    let reason: MailClaimResult['reason'];
    try { applied = (await apply(mail)) === true; if (!applied) reason = 'apply-false'; } catch { reason = 'apply-threw'; }
    if (!applied) return { id, ok: true, applied: false, acked: false, reason };
    const a = await this.call('POST', '/ack', { id });
    if (!a || !a.ok) {
      // 적용은 끝났고 ack만 못 했다: 다음 접속 목록에 다시 나오며 같은 연산 id라 두 번 들어가지 않는다
      return { id, ok: true, applied: true, acked: false, error: a ? errorOf(a.status, a.body) : 'offline' };
    }
    this.drop(id);
    return { id, ok: true, applied: true, acked: true };
  }

  /** 목록에 있는 우편을 차례로 모두 받는다(오프라인·빈도 제한이면 거기서 멈춘다) */
  async claimAll(apply: MailApply): Promise<MailClaimAllResult> {
    const out: MailClaimAllResult = { tried: 0, done: 0, failed: 0, results: [] };
    if (this.busy) return out;
    this.busy = true; this.revision++;
    try {
      for (const m of [...this.mail]) {
        const r = await this.claim(m.id, apply);
        out.tried++; out.results.push(r);
        if (r.acked) out.done++; else out.failed++;
        if (r.error === 'offline' || r.error === 'rate' || r.error === 'auth') { out.stopped = r.error; break; }
      }
    } finally {
      this.busy = false; this.revision++;
    }
    return out;
  }

  private drop(id: number): void {
    const n = this.mail.length;
    this.mail = this.mail.filter((m) => m.id !== id);
    if (this.mail.length !== n) this.revision++;
  }
}
